// frontend/client/src/components/Campaigns.js
import React from 'react';
import { Target, TrendingUp, Layers, Users, Eye, Heart, BarChart3, Plus } from 'lucide-react';

// FIX: --brand-purple, --brand-purple-light, --brand-gradient-soft, and
// --text-dark were never defined anywhere in the real design system
// (index.css / useTheme.js). Every reference fell back to nothing, so the
// campaign status pills, cross-platform distribution tags, and the "New
// Campaign" button were all silently unstyled.
const CP_CSS = `
  .cp-root { display: flex; flex-direction: column; gap: 20px; padding: 24px; font-family: var(--font-body); }
  .cp-header { display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
  .cp-title { font-family: var(--font-display); font-size: 24px; font-weight: 800; color: var(--text-main); margin: 0 0 4px; letter-spacing: -0.02em; }
  
  .cp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(350px, 1fr)); gap: 24px; }
  
  .cp-card { background: var(--bg-surface); border: 1px solid var(--border-light); border-radius: 16px; padding: 24px; box-shadow: var(--shadow-md); transition: transform 0.2s; }
  .cp-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-lg); }
  
  .cp-card-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; border-bottom: 1px solid var(--border-light); padding-bottom: 16px; }
  .cp-name { font-family: var(--font-display); font-size: 18px; font-weight: 700; color: var(--text-main); margin: 0 0 4px; }
  .cp-status { font-size: 10px; font-weight: 700; text-transform: uppercase; padding: 4px 10px; border-radius: 999px; background: rgba(16, 185, 129, 0.1); color: #10B981; }
  
  .cp-stats-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px; }
  .cp-stat-box { background: var(--bg-canvas); padding: 12px; border-radius: 12px; border: 1px solid var(--border-medium); }
  .cp-stat-label { font-size: 11px; color: var(--text-muted); font-weight: 700; text-transform: uppercase; display: flex; align-items: center; gap: 6px; margin-bottom: 4px; }
  .cp-stat-val { font-size: 20px; font-weight: 800; color: var(--text-main); margin: 0; }
  
  .cp-assets { display: flex; gap: 8px; flex-wrap: wrap; }
  .cp-asset-pill { background: var(--brand-secondary); color: var(--brand-secondary-contrast, var(--brand-primary)); border: 1px solid var(--brand-border); font-size: 11px; font-weight: 700; padding: 4px 10px; border-radius: 6px; }
`;

const DEMO_CAMPAIGNS = [
  {
    id: 'c1',
    name: 'Q3 Cybersecurity & AI Policy',
    description: 'Grouped content focusing on AI security narratives across LinkedIn and Twitter.',
    status: 'Active',
    metrics: { totalViews: '142.5K', avgEngagement: '8.4%', scheduledPosts: 12, completedPosts: 5 },
    platforms: ['LinkedIn', 'Twitter/X']
  },
  {
    id: 'c2',
    name: 'Product Launch: Symphony',
    description: 'Multi-channel blast for the new software tier. Includes highly formatted video cuts.',
    status: 'Active',
    metrics: { totalViews: '89.2K', avgEngagement: '12.1%', scheduledPosts: 8, completedPosts: 8 },
    platforms: ['TikTok', 'Instagram', 'YouTube']
  }
];

export default function Campaigns() {
  return (
    <div className="cp-root">
      <style>{CP_CSS}</style>
      
      <div className="cp-header">
        <div>
          <h1 className="cp-title"><Target color="var(--brand-accent)" style={{ marginRight: 8, verticalAlign: 'middle' }}/> Campaigns & Themes</h1>
          <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 14 }}>
            Stop measuring posts in isolation. Track the aggregate ROI of your narrative arcs.
          </p>
        </div>
        <button style={{ background: 'var(--text-main)', color: 'var(--bg-surface)', border: 'none', padding: '10px 16px', borderRadius: '999px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Plus size={16} /> New Campaign
        </button>
      </div>

      <div className="cp-grid">
        {DEMO_CAMPAIGNS.map(camp => (
          <div key={camp.id} className="cp-card">
            <div className="cp-card-header">
              <div>
                <h3 className="cp-name">{camp.name}</h3>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>{camp.description}</p>
              </div>
              <span className="cp-status">{camp.status}</span>
            </div>

            <div className="cp-stats-grid">
              <div className="cp-stat-box">
                <span className="cp-stat-label"><Eye size={12} /> Aggregate Reach</span>
                <p className="cp-stat-val">{camp.metrics.totalViews}</p>
              </div>
              <div className="cp-stat-box">
                <span className="cp-stat-label"><TrendingUp size={12} /> Avg Engagement</span>
                <p className="cp-stat-val">{camp.metrics.avgEngagement}</p>
              </div>
              <div className="cp-stat-box">
                <span className="cp-stat-label"><Layers size={12} /> Live Assets</span>
                <p className="cp-stat-val">{camp.metrics.completedPosts} / {camp.metrics.scheduledPosts}</p>
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>Cross-Platform Distribution</p>
              <div className="cp-assets">
                {camp.platforms.map(p => (
                  <span key={p} className="cp-asset-pill">{p}</span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}